/**
 * lib/west-bengal/pf.ts — WBSEDCL-only Apparent Energy (kVAh) & Power Factor
 * ===========================================================================
 * WB-ISOLATED MODULE. Do not import from Telangana code paths.
 *
 * ROOT CAUSE — for the WB / LK4405 sites (Taparia, Omega, JD Powercorn):
 *
 *  1. `apparentEnergyKvah()` in `lib/calculations.ts` divides the `energyVAh`
 *     register delta by 1000 (treats it as raw VAh). Per the VIREON-CORTEX
 *     Variables & Formulas Reference (§2.2, RAW sheet column AC), `energyVAh`
 *     is actually ALREADY IN kVAh — so that primary path under-computes by
 *     1000x and is effectively always rejected, falling back to trapezoidal
 *     integration of `vaTotal`, a column documented elsewhere in this
 *     codebase as unreliable for the LK4405.
 *
 *  2. The shared `HourlyLogPdfCard.tsx` computed the PDF's row-level Power
 *     Factor and "Overall Power Factor" as
 *         PF = kWhAccumulated / kVAhAccumulated
 *     where the two accumulators are built from two DIFFERENT integrators
 *     (kWh: trapezoidal Watts_Total; kVAh: the unreliable vaTotal fallback
 *     above). These can cross over (kWhAcc > kVAhAcc), giving PF > 1.
 *
 *  3. Separately, the Dashboard's "Yesterday"/"This Month" cards computed
 *     kVAh and PF from DIFFERENT row windows/anchors than the PDF and than
 *     each other — so kvahYesterday, pfYesterday, and the PDF's "kVAh Today"
 *     for the same calendar date could all disagree (e.g. 1.01 MVAh vs
 *     994.35 kVAh vs PF 0.992 vs PF 0.938), none of which were mutually
 *     consistent (kWh/kVAh ≠ displayed PF).
 *
 * FIX (WB only):
 *   - apparentEnergyKvahSlotWB(): kVAh for a window is the REGISTER DELTA of
 *     `energyVAh` (Δ vahReceived, NO /1000 — already kVAh per spec §2.2),
 *     falling back to apparentEnergyKvah() only if the register doesn't move
 *     across the whole window.
 *   - dailyKvahKwhWB() / mtdKvahKwhWB(): compute "kWh Today"/"kVAh Today" (and
 *     MTD totals) using the EXACT SAME per-hour-slot + bridge-row methodology
 *     as HourlyLogPdfCardWB's dailyHourSlots(). The Dashboard now calls these
 *     SAME functions, so its kVAh/kWh figures are byte-identical to the PDF
 *     for the same date.
 *   - powerFactorFromPair(): PF = kWh/kVAh derived from THAT SAME (kwh, kvah)
 *     pair — guaranteeing kVAh ≥ kWh and 0 ≤ PF ≤ 1, and that the displayed
 *     PF always matches the displayed kWh/kVAh.
 *   - clampKvahPair(): display-time safety net for the PDF's per-row and
 *     End-of-Day PF figures (used by HourlyLogPdfCardWB.tsx).
 */

import type { TelemetryRow } from '@/types';
import { energyDelivered, apparentEnergyKvah, safe } from '@/lib/calculations';

/**
 * Per-hour-slot / window apparent energy (kVAh) for WB meters.
 *
 * Primary: register-delta of energyVAh (already kVAh per spec §2.2 AC) —
 *   kVAh = energyVAh(last row) − energyVAh(first row).
 * Fallback: apparentEnergyKvah(rows) (trapezoidal/vaTotal) — used only when
 *   the energyVAh register doesn't move across the WHOLE window (flat or
 *   unavailable for this dataset).
 *
 * `rows` should be supplied exactly as the kWh integrator (energyDelivered)
 * expects — including any bridge/anchor rows.
 */
export function apparentEnergyKvahSlotWB(rows: TelemetryRow[]): number {
  if (rows.length < 2) return 0;
  const sorted = [...rows].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  const first = safe(sorted[0].energyVAh);
  const last  = safe(sorted[sorted.length - 1].energyVAh);
  const delta = last - first;
  // energyVAh on raw rows is stored in VAh — divide by 1000 to get kVAh.
  if (delta > 0) return delta / 1000;
  // Register flat for this window — fall back to trapezoidal method.
  return apparentEnergyKvah(rows);
}

/**
 * Clamp an already-computed (kwh, kvah) pair to satisfy the physical
 * constraints kVAh ≥ kWh and 0 ≤ PF ≤ 1. Used by the WB hourly PDF as a
 * display-time safety net (per-row and End-of-Day PF figures).
 */
export function clampKvahPair(kwh: number, kvah: number): { kwh: number; kvah: number; pf: number } {
  const safeKwh  = Math.max(0, safe(kwh));
  const safeKvah = Math.max(safe(kvah), safeKwh);
  const pf = safeKvah > 0 ? Math.min(Math.max(safeKwh / safeKvah, 0), 1) : 0;
  return { kwh: safeKwh, kvah: safeKvah, pf };
}

/**
 * Total kWh/kVAh for ONE calendar day, computed via the SAME per-hour-slot
 * methodology as the WB Hourly Power Log PDF (HourlyLogPdfCardWB):
 *
 *   bridgeRow = last row of the PREVIOUS calendar day (anchors midnight)
 *   baseline  = integrate([bridgeRow])  (= 0 for a single-row input)
 *   dayTotal  = max(0, integrate([bridgeRow, ...allRowsOfDay]) - baseline)
 *
 * Using this exact methodology for the Dashboard's "Today"/"Yesterday" cards
 * guarantees they match the PDF's "kWh Today" / "kVAh Today" totals for the
 * same date — both are now the literal same calculation.
 *
 * @param allRows  Full chronologically-sortable row set for the meter
 *                 (any order — sorted internally).
 * @param dateStr  YYYY-MM-DD calendar date to total.
 */
export function dailyKvahKwhWB(allRows: TelemetryRow[], dateStr: string): { kwh: number; kvah: number } {
  const sorted = [...allRows].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

  const dayRows = sorted.filter(r => r.date === dateStr);
  if (dayRows.length < 1) return { kwh: 0, kvah: 0 };

  const prevDate = (() => {
    const d = new Date(dateStr + 'T00:00:00');
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  })();
  const prevRows  = sorted.filter(r => r.date === prevDate);
  const bridgeRow = prevRows.length ? [prevRows[prevRows.length - 1]] : [];

  const kwh  = Math.max(0, energyDelivered([...bridgeRow, ...dayRows]) - energyDelivered([...bridgeRow]));
  const kvah = Math.max(0, apparentEnergyKvahSlotWB([...bridgeRow, ...dayRows]) - apparentEnergyKvahSlotWB([...bridgeRow]));

  return { kwh, kvah: Math.max(kvah, kwh) };
}

/**
 * Total kWh/kVAh for a Month-To-Date window, computed as the sum of
 * dailyKvahKwhWB() over every calendar date in `dateStrs`. Summing per-day
 * totals (each computed via the PDF's slot methodology) keeps the MTD card
 * consistent with both the daily cards and the per-day PDFs.
 *
 * @param allRows   Full chronologically-sortable row set for the meter.
 * @param dateStrs  Sorted, deduplicated list of YYYY-MM-DD dates to include
 *                   (typically every date in the current month up to today).
 */
export function mtdKvahKwhWB(allRows: TelemetryRow[], dateStrs: string[]): { kwh: number; kvah: number } {
  let kwh = 0;
  let kvah = 0;
  for (const d of dateStrs) {
    const day = dailyKvahKwhWB(allRows, d);
    kwh  += day.kwh;
    kvah += day.kvah;
  }
  return { kwh, kvah: Math.max(kvah, kwh) };
}

/**
 * WB Power Factor for a {kwh, kvah} pair (e.g. from dailyKvahKwhWB /
 * mtdKvahKwhWB). PF = kWh / kVAh, always in [0, 1].
 */
export function powerFactorFromPair(kwh: number, kvah: number): number {
  if (kvah <= 0) return 0;
  return Math.min(Math.max(kwh / kvah, 0), 1);
}
